// The hold'em screen (casino.md, ECONOMY · CASINO).
//
// Six casino seats, one board, one pot and one bar. The screen rules on
// nothing: `CasinoTable.allowed_actions` states what the reader may do,
// `to_call` states what the reader owes and `min_raise` states the floor
// of a raise. Every control the frame does not allow is `disabled`, and a
// refused act comes back as a notice, never as a picture this file made
// up.
//
// **The one piece of state this screen owns is the bet size.** A size the
// reader dialled in is an intent the wire has not seen, so it lives here
// and not in the slice. It goes back to the minimum raise whenever the
// betting round moves — a new hand, a new street, or an opponent who
// raised the floor — because a size that stood through a raise would send
// a smaller bet than the reader read on the pill.
//
// **The seat ring is a 3-wide, 2-tall grid and the reader sits
// bottom-centre.** A player reads a table from the own seat outward, so
// the ring rotates under the reader instead of the reader moving around a
// fixed ring. `SEAT_RING` states the cell of each step clockwise from the
// reader, so the six cells read as a ring and not as a list.
//
// **The turn bar is quantized to whole percent.** The overlay redraws its
// body at 4 Hz (ui-map-interface.md §10.3) and the panel writes the host
// only when the markup changed, so a bar at full precision would rewrite
// the screen on every pass. Two decimals of `scaleX` is one percent, which
// is the same quantization the media player's progress bar takes.
//
// **The controls stand only while the reader can act** (R2142). Between
// hands and out of turn the dock at the foot holds one waiting line that
// says what the table waits for: more players, the next deal and its
// time, the seat at turn, and when the reader acts. On the reader's own
// turn the dock carries the countdown beside the thumb, and the casino
// live region says the start of the turn and its last quarter once each.
// A FOLD that gives a hand up for nothing — a CHECK is free — takes two
// presses, and the line says TIME when the table folded the hand for the
// reader (`foldCasinoTable` of `casinostate.ts` reads that fold).

import type { MapKeyId } from '../../gen/presentation';
import { CASINO_TIMER_WARN_FRAC } from '../../gen/presentation';
import {
  CasinoAction, CasinoPhase, type CasinoResult, type CasinoRules,
  type CasinoSeatResult, type CasinoSeatView, type CasinoStake, type CasinoTable,
} from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { groupThousands } from '../../locale/group';
import { armFaces } from '../choice/choice';
import { esc } from '../esc';
import {
  casinoActFrac, casinoActLeftSec, casinoHandKey, casinoOffers, casinoSeatOf, casinoTimedOut, stakeAt,
} from './casinostate';
import { part, type GameCtx, type GameView } from './gameview';
import { pct } from './pct';
import { tagSpeech, resultTag, handWord, sayAttrs } from './moment';
import { armedCtl, card, cards, ctl, seatData, seatName } from './cards';
import { seatBadge } from './club';

/** The seats of one hold'em table (casino.md §2). The grid holds six cells. */
export const POKER_SEATS = 6;

/** How many community cards a full board holds. */
const BOARD_PLACES = 5;

/**
 * The grid cell of each step clockwise from the reader.
 *
 * The grid reads 0 1 2 across the top row and 3 4 5 across the bottom, so
 * cell 4 is bottom-centre and belongs to the reader. Clockwise from there
 * the ring runs bottom-right, top-right, top-centre, top-left and back to
 * bottom-left, which is the seat that acts before the reader.
 */
const SEAT_RING = [4, 5, 2, 1, 0, 3];

/** The arm key of an all-in send. It must match what `Overlay.armKey` builds. */
const ALL_IN_KEY = 'casino:allin';

/** The arm key of a FOLD while a CHECK is free (R2142). The button's
 *  `data-id` is `fold`, so `Overlay.armKey` builds this key. */
const FOLD_KEY = 'casino:fold';

/** The six acts of a turn. The bar stands while the frame allows one. */
const TURN_ACTS: readonly CasinoAction[] = [
  CasinoAction.CASINO_ACTION_FOLD, CasinoAction.CASINO_ACTION_CHECK,
  CasinoAction.CASINO_ACTION_CALL, CasinoAction.CASINO_ACTION_BET,
  CasinoAction.CASINO_ACTION_RAISE, CasinoAction.CASINO_ACTION_ALL_IN,
];

/** The four sizes the preset pills set (casino.md §2). */
export interface PokerPresets {
  /** The smallest legal raise. */
  min: number;
  /** Half of the pot as it stands after the reader calls. */
  half: number;
  /** The pot as it stands after the reader calls. */
  pot: number;
  /** Every chip the reader has at the table. */
  all: number;
}

/**
 * The four preset sizes, each one clamped into `[minRaise, chips]`.
 *
 * A pot-sized raise is the call plus the pot as it stands AFTER that call,
 * which is the classical sizing and also the plain reading when nothing is
 * owed. Every size is a whole credit, because the wire carries credits and
 * a half credit does not exist.
 *
 * A seat too short to make the minimum raise reads every pill at its whole
 * stack, which is the only legal size left to it.
 */
export function betPresets(
  pot: number, chips: number, minRaise: number, toCall: number,
): PokerPresets {
  const stack = wholeCredits(chips);
  const owed = wholeCredits(toCall);
  const floor = wholeCredits(minRaise);
  const after = wholeCredits(pot) + owed;
  const clamp = (v: number) => Math.min(stack, Math.max(floor, Math.round(v)));
  return {
    min: clamp(floor),
    half: clamp(owed + after / 2),
    pot: clamp(owed + after),
    all: clamp(stack),
  };
}

/** A finite number, or zero. The wire may carry a field this client is new to. */
function whole(v: number): number {
  return Number.isFinite(v) ? v : 0;
}

/** A whole, non-negative count of credits, as the wire carries one. */
function wholeCredits(v: number): number {
  return Math.max(0, Math.floor(whole(v)));
}

/** What the reader owes to stay in the hand. */
function owedOf(t: CasinoTable): number {
  return wholeCredits(t.toCall);
}

/** The four presets for the reader's own seat at this table. */
function presetsOf(t: CasinoTable, me: CasinoSeatView | null): PokerPresets {
  return betPresets(t.pot, me?.chips ?? 0, t.minRaise, t.toCall);
}

/** One seat's line of the settled hand, or undefined. */
function seatResult(res: CasinoResult | null, seat: number): CasinoSeatResult | undefined {
  return res?.seats.find((r) => r.seat === seat);
}

/** The showdown line of one seat, or '' while the server stated none. */
function rankLineOf(res: CasinoResult | null, seat: number): string {
  return seatResult(res, seat)?.handRankLine ?? '';
}

/**
 * The six grid cells, with the reader bottom-centre and a vacant casino
 * seat as null.
 *
 * A seat number counts from 0, so 0 is a real chair and `yourSeat` says
 * "nobody" with the seat count itself (R1040). A watcher therefore
 * anchors on seat 0, so the ring still reads as a stable picture.
 */
export function seatOrder(
  seats: CasinoSeatView[], yourSeat: number,
): (CasinoSeatView | null)[] {
  const cells: (CasinoSeatView | null)[] = [null, null, null, null, null, null];
  const anchor = yourSeat >= 0 && yourSeat < POKER_SEATS ? yourSeat : 0;
  for (const s of seats) {
    if (s.seat < 0 || s.seat >= POKER_SEATS) continue;
    const step = (((s.seat - anchor) % POKER_SEATS) + POKER_SEATS) % POKER_SEATS;
    cells[SEAT_RING[step]] = s;
  }
  return cells;
}

/**
 * True while the frame allows this act AND the reader's seat holds the
 * turn (R988).
 *
 * The bar was live out of turn, because the frame carried the acts of the
 * LAST turn on. The two facts are both on the wire, so a control reads
 * both: FOLD, CHECK, CALL, BET, RAISE and ALL IN answer only to the seat
 * the table is waiting for.
 */
function allows(t: CasinoTable, a: CasinoAction): boolean {
  return myTurn(t) && casinoOffers(t, a);
}

/** True while the reader's own seat holds the turn. */
function myTurn(t: CasinoTable): boolean {
  const me = casinoSeatOf(t);
  return me !== null && me.seat === t.turnSeat;
}

/**
 * True while the reader can act: the own seat holds the turn and the
 * frame allows one act of the turn (R2142). The bar stands only then.
 */
function pokerCanAct(t: CasinoTable): boolean {
  return TURN_ACTS.some((a) => allows(t, a));
}

/**
 * True while a FOLD gives the hand up for nothing: nothing is owed and a
 * CHECK is free. FOLD then takes two presses (R2142). A reader who must
 * call to stay in folds with one press.
 */
function foldGuarded(t: CasinoTable): boolean {
  return owedOf(t) === 0 && allows(t, CasinoAction.CASINO_ACTION_CHECK);
}

/** True between hands, which is when a seat may top its chips up. */
function betweenHands(t: CasinoTable): boolean {
  return t.phase === CasinoPhase.CASINO_PHASE_SETTLED
    || t.phase === CasinoPhase.CASINO_PHASE_WAITING;
}

/** True while the reader may set a size at all: a bet, a raise or an all-in. */
function canSize(t: CasinoTable): boolean {
  return allows(t, CasinoAction.CASINO_ACTION_BET)
    || allows(t, CasinoAction.CASINO_ACTION_RAISE)
    || allows(t, CasinoAction.CASINO_ACTION_ALL_IN);
}

/**
 * True while the size the bar carries is the whole stack and the frame
 * allows an all-in: the one send that arms first (R837). The bar and the
 * send read one rule, so the label never promises a send the press does
 * not make.
 */
function allInSend(p: PokerPresets, size: number, canAllIn: boolean): boolean {
  return size >= p.all && p.all > 0 && canAllIn;
}

/** What one seat still needs to reach the buy-in ceiling, in credits. */
function topUpWant(stake: CasinoStake, me: CasinoSeatView | null): number {
  return Math.max(0, stake.buyInMax - (me?.chips ?? 0));
}

/** The result of the hand the table frame describes, or null. */
function resultOf(t: CasinoTable, r: CasinoResult | null): CasinoResult | null {
  if (!r || r.tableId !== t.id || r.handId !== t.handId) return null;
  return r;
}

// --- the local bet size -------------------------------------------------

/** The size the reader dialled in, in credits. */
let localSize = 0;
/** The betting round that size belongs to. A new round takes it back to the min. */
let sizeKey = '';

/**
 * Puts the bet size back to its unset state.
 *
 * The panel calls it when the reader gives a seat up, so the next table
 * starts at its own minimum, and a test calls it between cases.
 */
export function pokerResetSize(): void {
  localSize = 0;
  sizeKey = '';
}

/** What names one betting round: the hand, the street and the floor to flash. */
function roundKey(t: CasinoTable): string {
  return `${t.id}:${t.handId}:${t.board.length}:${t.toCall}:${t.minRaise}`;
}

/**
 * The size the bar sends now.
 *
 * It resets to the minimum raise whenever the betting round moved, and it
 * is always inside the legal window, so the pill, the slider, the readout
 * and the intent state one figure.
 */
function sizeNow(t: CasinoTable, p: PokerPresets): number {
  const key = roundKey(t);
  if (key !== sizeKey) {
    sizeKey = key;
    localSize = p.min;
  }
  return clampSize(p, Math.round(whole(localSize)));
}

/** One size held inside the legal window. */
function clampSize(p: PokerPresets, v: number): number {
  return Math.min(p.all, Math.max(p.min, v));
}

/**
 * The reader dialled a size in, on a pill or on the slider. It lands
 * inside the legal window of the round that stands, and it sends nothing:
 * the size travels on the next press of the send button.
 */
function setSize(t: CasinoTable, p: PokerPresets, v: number): void {
  sizeNow(t, p);
  localSize = clampSize(p, Number.isFinite(v) ? v : p.min);
}

// --- the markup ---------------------------------------------------------

/** A timer of the turn: one style write, quantized to a percent. `cls`
 *  holds the more classes, each with its leading space. */
function timerHtml(cls: string, frac: number): string {
  return `<div class="casino-timer${cls}">`
    + `<i class="timer-fill" style="transform:scaleX(${frac.toFixed(2)})"></i></div>`;
}

/** The turn bar of the seat at turn. */
function turnBar(ctx: GameCtx): string {
  const frac = casinoActFrac(ctx.slice, ctx.nowMs);
  return timerHtml(frac <= CASINO_TIMER_WARN_FRAC ? ' turnbar warn' : ' turnbar', frac);
}

/** One cell of the seat ring. A vacant casino seat draws an empty cell. */
function seatCell(
  s: CasinoSeatView | null, t: CasinoTable, ctx: GameCtx, res: CasinoResult | null,
  meSeat: number,
): string {
  if (!s) return '<div class="seat empty"></div>';
  const cls = ['seat'];
  if (s.seat === t.turnSeat) cls.push('turn');
  if (s.isYou) cls.push('you');
  if (s.folded) cls.push('folded');
  // The seat that took the pot wears the win flash of the result (R2115).
  const won = !!res && !res.aborted && (seatResult(res, s.seat)?.delta ?? 0) > 0;
  if (won) cls.push('won');
  // A fictive player and an autopilot empire read as players at the table
  // (R979, R1050): `cards.ts` holds the one seat-name rule, so the three
  // screens print one word for one seat. The flags stay on the wire for
  // the operator's view and ride here as data attributes only (R1352).
  const name = seatName(s);
  // "D" is the dealer button, a symbol of the game like the rank letters
  // on the cards. The sentence that states where it sits is on the meta
  // line above the felt.
  const dealer = s.seat === t.buttonSeat
    ? `<b class="chip dealer" title="${esc(S.poker_button({ n: s.seat }))}">D</b>` : '';
  const bet = s.bet > 0 ? `<span class="chip">${groupThousands(s.bet)}</span>` : '';
  // The all-in is the server's fact (`CasinoSeatView.all_in`), read and
  // never derived: a stack that reads zero is not the same fact.
  const state = s.folded
    ? `<span class="seat-state">${S.casino_seat_folded}</span>`
    : s.allIn
      ? `<span class="seat-state">${S.poker_all_in}</span>`
      : '';
  // At the showdown every hand that is still live is face up, and the
  // server states what each one holds in the reader's own language (R858).
  // The reader's own two cards and their line stand ONCE, large, in the
  // hole box under the felt, so the own seat draws neither (R2142).
  const own = s.seat === meSeat;
  const line = own ? '' : rankLineOf(res, s.seat);
  const shown = s.cards.length > 0 && !own
    ? `<div class="seat-cards">${cards(s.cards)}</div>` : '';
  const rank = line !== '' ? `<div class="seat-rank">${esc(line)}</div>` : '';
  return `<div class="${cls.join(' ')}"${seatData(s)}${won ? ` data-flash="${ctx.slice.resultSeq}"` : ''}>`
    + `<div class="seat-name">${seatBadge(s.faction)}${name}${dealer}</div>`
    + `<div class="seat-chips">${S.casino_chips({ amount: groupThousands(s.chips) })}</div>`
    + shown + rank
    + `<div class="seat-foot">${bet}${state}</div>`
    + (s.seat === t.turnSeat ? turnBar(ctx) : '')
    + '</div>';
}

/** The board and the pot, in the middle of the felt. */
function boardBox(t: CasinoTable): string {
  let cards = '';
  for (let i = 0; i < BOARD_PLACES; i++) cards += card(t.board[i] ?? 0, { slot: true });
  return '<div class="board">'
    + `<div class="board-head">${S.poker_board}</div>`
    + `<div class="board-cards">${cards}</div>`
    + `<div class="pot">${S.casino_pot({ amount: groupThousands(t.pot) })}</div>`
    + '</div>';
}

/** The reader's own two cards, larger than a seat's, and the showdown line. */
function holeBox(t: CasinoTable, me: CasinoSeatView | null, res: CasinoResult | null): string {
  const hole = t.yourHole.length > 0
    ? cards(t.yourHole, { big: true })
    : card(0, { big: true, slot: true }).repeat(2);
  const line = me ? rankLineOf(res, me.seat) : '';
  const rank = line !== '' ? `<div class="hole-rank">${esc(line)}</div>` : '';
  return `<div class="hole-head">${S.poker_your_hand}</div>`
    + `<div class="hole-cards">${hole}</div>${rank}`;
}

/**
 * The TOP UP row between hands (casino.md §2, R977). A seat tops up
 * between two hands alone, so a running hand draws no row (R2142).
 */
function topUpRow(ctx: GameCtx, t: CasinoTable, me: CasinoSeatView | null): string {
  const stake = stakeAt(ctx.rules, t.stakeTier);
  if (!me || !stake || !betweenHands(t)) return '';
  const want = topUpWant(stake, me);
  const afford = ctx.credits >= want;
  const on = want > 0 && afford && casinoOffers(t, CasinoAction.CASINO_ACTION_TOP_UP);
  // A seat that is full reads the ceiling, and so does one that can pay.
  const why = want > 0 && !afford
    ? S.casino_no_buy_in : S.poker_top_up_to({ amount: groupThousands(stake.buyInMax) });
  return part('topup', 'class="topup"',
    ctl('ptopup', S.poker_top_up, on, afford ? '' : 'noafford')
    + `<span class="topup-why">${why}</span>`);
}

/**
 * The house share of a pot and its cap at one stake tier, from the rule
 * table (R2142). The cap is a count of big blinds, so it reads in the
 * credits of the tier. Empty before the hello arrived.
 */
function rakeLine(rules: CasinoRules | null, stake: CasinoStake | null): string {
  if (!rules || !stake) return '';
  return S.poker_rake({
    pct: pct(rules.rakePct * 100),
    cap: groupThousands(rules.rakeCapBb * stake.minBet),
  });
}

/**
 * The line the reader reads after a settled hand: the result tag, the
 * server's line and the rake (R2115). A hand the reader put no chip in
 * draws no result tag and says that there was no bet. The part is the result
 * line the live region says.
 */
function resultRow(ctx: GameCtx, t: CasinoTable, me: CasinoSeatView | null): string {
  const res = resultOf(t, ctx.slice.result);
  if (!res) return '';
  const seq = ctx.slice.resultSeq;
  if (res.aborted) {
    const gone = `${S.casino_table_gone} · ${S.casino_stakes_back}`;
    return part('presult', `class="poker-result"${sayAttrs(seq, gone)}`, gone);
  }
  const mine = me ? seatResult(res, me.seat) : undefined;
  // The house takes its share of a pot that saw a flop, and the sentence
  // states the rule the rules table carries, never a figure written here.
  const house = rakeLine(ctx.rules, stakeAt(ctx.rules, t.stakeTier));
  const rake = res.rake > 0 && house !== '' ? ` · ${house}` : '';
  const line = res.line !== '' ? ` · ${esc(res.line)}` : '';
  if (!mine) {
    return part('presult', `class="poker-result"${sayAttrs(seq, S.casino_no_bet)}`,
      `<span class="nobet">${S.casino_no_bet}</span>${line}${rake}`);
  }
  const word = handWord(mine.delta);
  return part('presult', `class="poker-result"${sayAttrs(seq, tagSpeech(mine.delta, word))}`,
    `${resultTag(mine.delta, word, seq)}<span class="say">${line}${rake}</span>`);
}

/**
 * The meta line above the felt: the blinds and where the button sits,
 * and under it the house share of a pot and its cap (R2142).
 */
function metaRow(ctx: GameCtx, t: CasinoTable): string {
  const stake = stakeAt(ctx.rules, t.stakeTier);
  const big = stake ? stake.minBet : t.minBet;
  // The rule table states BOTH blinds (`CasinoStake.small_blind`), so a
  // house that runs another ratio than one half states it there, and the
  // line still reads true. Only when the table states no small blind (no
  // rule table yet, or a zero) does the screen halve the big blind.
  const small = stake && stake.smallBlind > 0
    ? stake.smallBlind : Math.floor(big / 2);
  const blinds = big > 0
    ? S.poker_blinds({ small: groupThousands(small), big: groupThousands(big) })
    : '';
  const button = t.buttonSeat > 0 ? ` · ${S.poker_button({ n: t.buttonSeat })}` : '';
  const house = rakeLine(ctx.rules, stake);
  return `<span class="meta-line">${blinds}${button}</span>`
    + (house === '' ? '' : `<span class="poker-house">${house}</span>`);
}

// --- the dock: the turn line and the bar (R2142) -------------------------

/** The whole seconds the seat at turn still has, now. */
function leftSec(ctx: GameCtx): number {
  return Math.ceil(casinoActLeftSec(ctx.slice, ctx.nowMs));
}

/**
 * True while the reader's own turn runs low: its last share
 * `CASINO_TIMER_WARN_FRAC` of the whole turn the rule table states. The
 * frame's own count is the fallback before the hello arrived.
 */
function turnLow(ctx: GameCtx, t: CasinoTable): boolean {
  const span = ctx.rules && ctx.rules.actSec > 0 ? ctx.rules.actSec : t.actLeftSec;
  return casinoActLeftSec(ctx.slice, ctx.nowMs) <= span * CASINO_TIMER_WARN_FRAC;
}

/**
 * The cue attributes: the panel says `speech` in its live region once a
 * `key` (R2142). A key names one turn and one moment of it, so the start
 * and the last quarter of one turn each speak once.
 */
function cueAttrs(key: string, speech: string): string {
  return ` data-cue="${esc(key)}" data-speech="${esc(speech)}"`;
}

/** The seat a seat number names, or undefined. */
function seatAt(t: CasinoTable, n: number): CasinoSeatView | undefined {
  return t.seats.find((s) => s.seat === n);
}

/** True while a seat is in the hand, has not folded and has chips to act with. */
function actsInHand(s: CasinoSeatView): boolean {
  return !s.sittingOut && !s.folded && !s.allIn;
}

/**
 * The seat that acts just before the reader, counted clockwise from the
 * seat at turn, or null when the reader acts next.
 *
 * It answers only while the reader still owes chips: a bet stands over
 * the reader's own, so the betting round cannot close before the reader
 * acts, and the order of the ring is the order of the turns. A reader who
 * owes nothing may already have acted, and the screen then names no
 * order, because an order it cannot know would be a false promise.
 */
function seatBefore(t: CasinoTable, me: CasinoSeatView): CasinoSeatView | null | undefined {
  if (owedOf(t) === 0 || !actsInHand(me)) return undefined;
  if (!seatAt(t, t.turnSeat)) return undefined;
  let before: CasinoSeatView | null = null;
  for (let step = 1; step < POKER_SEATS; step++) {
    const n = (t.turnSeat + step) % POKER_SEATS;
    if (n === me.seat) return before;
    const s = seatAt(t, n);
    if (s && actsInHand(s)) before = s;
  }
  return undefined;
}

/** How many seats hold chips: a hand needs two. */
function stacks(t: CasinoTable): number {
  return t.seats.filter((s) => s.chips > 0).length;
}

/** What the table waits for, while the reader cannot act (R2142). */
function waitText(ctx: GameCtx, t: CasinoTable, me: CasinoSeatView | null): string {
  if (t.phase === CasinoPhase.CASINO_PHASE_SHOWDOWN) return S.poker_showdown;
  const turn = seatAt(t, t.turnSeat);
  if (betweenHands(t) || !turn) {
    if (stacks(t) < 2) return S.poker_wait_players;
    const sec = leftSec(ctx);
    return sec > 0 ? S.poker_wait_deal({ sec }) : S.poker_wait_soon;
  }
  const who = S.poker_wait_turn({ name: seatName(turn), sec: leftSec(ctx) });
  if (!me) return who;
  if (me.allIn) return `${who} · ${S.poker_you_all_in}`;
  if (me.folded) return `${who} · ${S.poker_wait_folded}`;
  const before = seatBefore(t, me);
  if (before === null) return `${who} · ${S.poker_wait_next}`;
  if (before) return `${who} · ${S.poker_wait_after({ name: seatName(before) })}`;
  return who;
}

/**
 * The turn line of the dock: the countdown of the reader's own turn with
 * its bar, or the one waiting line. It is a small part of its own,
 * because it steps with the clock (`gameview.ts` rule 3).
 */
function turnRow(ctx: GameCtx, t: CasinoTable, me: CasinoSeatView | null): string {
  if (pokerCanAct(t)) {
    const sec = leftSec(ctx);
    const low = turnLow(ctx, t);
    const turnKey = `${roundKey(t)}:${t.turnSeat}`;
    const cue = low
      ? cueAttrs(`${turnKey}:low`, S.poker_say_low({ sec }))
      : cueAttrs(`${turnKey}:turn`, S.poker_say_turn({ sec }));
    return part('turn', `class="poker-turn mine${low ? ' low' : ''}"${cue}`,
      `<span class="turn-state">${S.casino_your_turn}`
      + ` · ${S.casino_turn_left({ sec })}</span>`
      + timerHtml(low ? ' warn' : '', casinoActFrac(ctx.slice, ctx.nowMs)));
  }
  // The timeout fold of this hand stands until the next deal (R2142). The
  // cue keys on the folded hand, so the wait of the next hand, which
  // carries the next hand id, does not say it again.
  const lapsed = casinoTimedOut(ctx.slice, t) && !!me;
  const timeout = lapsed ? `<span class="poker-timeout">${S.poker_timed_out}</span>` : '';
  const cue = lapsed ? cueAttrs(`${ctx.slice.timedOut}:timeout`, S.poker_timed_out) : '';
  return part('turn', `class="poker-turn"${cue}`,
    `${timeout}<span class="turn-state">${waitText(ctx, t, me)}</span>`);
}

/**
 * The send button's face: one verb, BET or RAISE, and ALL IN only while
 * the size is the whole stack (R2142). All the faces stand in one grid
 * cell, so the press keeps the width of the widest and never jumps; the
 * amount stands as its own figure beside the slider.
 */
function sendFace(ctx: GameCtx, owed: number, allIn: boolean): string {
  const verb = owed > 0 ? S.poker_raise : S.poker_bet;
  return armFaces(verb, ctx.armLabel(ALL_IN_KEY, S.poker_all_in, S.poker_all_in_armed), allIn);
}

/**
 * The bar: FOLD, CHECK or CALL, BET or RAISE, the presets and the slider.
 * It is drawn only while the reader can act (R2142).
 */
function barRow(
  ctx: GameCtx, t: CasinoTable, p: PokerPresets, size: number,
): string {
  const owed = owedOf(t);
  const canFold = allows(t, CasinoAction.CASINO_ACTION_FOLD);
  const canCheck = allows(t, CasinoAction.CASINO_ACTION_CHECK);
  const canCall = allows(t, CasinoAction.CASINO_ACTION_CALL);
  const canBet = allows(t, CasinoAction.CASINO_ACTION_BET);
  const canRaise = allows(t, CasinoAction.CASINO_ACTION_RAISE);
  const canAllIn = allows(t, CasinoAction.CASINO_ACTION_ALL_IN);
  // The one send the size feeds. It becomes the whole stack when the size
  // reached it, and an all-in is a destructive control, so it takes two
  // presses like every other one (R837).
  const allIn = allInSend(p, size, canAllIn);
  const canSend = allIn || (owed > 0 ? canRaise : canBet);
  const sendExtra = allIn ? ' data-id="allin" data-arm="1"' : '';
  const sendCls = allIn && ctx.armed(ALL_IN_KEY) ? 'primary send armed' : 'primary send';
  // A FOLD while a CHECK is free gives the hand up for nothing, so it
  // arms first and names the act on its armed face (R2142, R2069).
  const fold = foldGuarded(t)
    ? armedCtl('pfold', 'fold', ctx.armLabel(FOLD_KEY, S.poker_fold, S.poker_fold_armed),
      ctx.armed(FOLD_KEY), 'danger', canFold ? '' : ' disabled')
    : ctl('pfold', S.poker_fold, canFold, 'danger');
  // The presets and the slider set the size the send button carries, so
  // they take the same gate: a size dialled out of turn would read as a
  // bet the reader may make (R988).
  const sizeOk = canBet || canRaise || canAllIn;
  const pill = (amount: number, label: string) =>
    ctl('ppreset', label, sizeOk, 'preset', ` data-id="${amount}"`);
  const hi = Math.max(p.min, p.all);
  const slider = `<input class="slider" type="range" data-act="casino" data-op="size"`
    + ` min="${p.min}" max="${hi}" step="1" value="${size}"${sizeOk ? '' : ' disabled'}>`;
  return '<div class="bar-acts">'
    + fold
    + (owed > 0
      ? ctl('pcall', S.poker_call_amount({ amount: groupThousands(owed) }), canCall)
      : ctl('pcheck', S.poker_check, canCheck))
    + ctl('pbet', sendFace(ctx, owed, allIn), canSend, sendCls, sendExtra)
    + '</div>'
    + '<div class="bar-sizes">'
    + pill(p.min, S.poker_min) + pill(p.half, S.poker_half_pot)
    + pill(p.pot, S.poker_pot) + pill(p.all, S.poker_all_in)
    + '</div>'
    + `<div class="bar-size">${slider}`
    + `<span class="size"><span class="size-k">${S.poker_amount}</span>`
    + ` <b class="size-n">${groupThousands(size)}</b></span></div>`;
}

// --- the acts -----------------------------------------------------------

/**
 * Sends one hold'em act. Every send goes through here, key or button. A
 * FOLD notes its hand in the slice, so the fold that comes back is never
 * read as the timeout fold (R2142).
 */
function send(ctx: GameCtx, action: CasinoAction, amount = 0): void {
  const t = ctx.slice.table;
  if (action === CasinoAction.CASINO_ACTION_FOLD && t) ctx.slice.foldSent = casinoHandKey(t);
  ctx.send({ kind: 'act', action, amount });
}

/**
 * Sends one act while the frame allows it. True either way: the button
 * took the release, and a press the frame did not allow sends nothing.
 */
function sendIf(ctx: GameCtx, t: CasinoTable, action: CasinoAction, amount = 0): true {
  if (allows(t, action)) send(ctx, action, amount);
  return true;
}

/**
 * The FOLD, by button or by key. While a CHECK is free it arms first
 * (R2142); the first press only arms. True means the screen took it.
 */
function sendFold(ctx: GameCtx, t: CasinoTable): boolean {
  if (!allows(t, CasinoAction.CASINO_ACTION_FOLD)) return false;
  if (foldGuarded(t) && !ctx.press(FOLD_KEY)) return true;
  send(ctx, CasinoAction.CASINO_ACTION_FOLD);
  return true;
}

/**
 * The BET, RAISE or ALL IN send.
 *
 * An all-in arms first (R837), so the whole stack never leaves on one
 * press. True means the screen took the release, armed or sent.
 */
function sendSize(ctx: GameCtx, t: CasinoTable, key: string): boolean {
  const p = presetsOf(t, casinoSeatOf(t));
  const size = sizeNow(t, p);
  if (allInSend(p, size, allows(t, CasinoAction.CASINO_ACTION_ALL_IN))) {
    if (!ctx.press(key)) return true;
    send(ctx, CasinoAction.CASINO_ACTION_ALL_IN, p.all);
    return true;
  }
  const action = owedOf(t) > 0
    ? CasinoAction.CASINO_ACTION_RAISE : CasinoAction.CASINO_ACTION_BET;
  if (!allows(t, action)) return true;
  send(ctx, action, size);
  return true;
}

/**
 * The hold'em screen.
 *
 * A module object and not a class: the panel holds one screen a game and
 * the bet size belongs to the screen, so the state sits beside the three
 * methods and no caller ever builds a second one.
 */
export const pokerView: GameView = {
  html(ctx: GameCtx): string {
    const t = ctx.slice.table;
    if (!t) return `<div class="empty">${S.casino_no_table}</div>`;
    if (t.phase === CasinoPhase.CASINO_PHASE_DISSOLVED) {
      return `<div class="casino-felt poker"><div class="empty">${S.casino_table_gone}`
        + ` · ${S.casino_stakes_back}</div></div>`;
    }
    const me = casinoSeatOf(t);
    const res = resultOf(t, ctx.slice.result);
    // `casinoSeatOf` already read `is_you` and then `your_seat`, so the
    // anchor is the reader's own seat wherever the frame names one.
    const cells = seatOrder(t.seats, me?.seat ?? 0);
    const ring = cells.map((s) => seatCell(s, t, ctx, res, me?.seat ?? -1)).join('');
    const p = presetsOf(t, me);
    const size = sizeNow(t, p);
    // The parts of the felt (`gameview.ts`). The ring carries the turn
    // bar, so it is the part that steps with the clock; the board, the
    // hole cards and the acts stand until the hand moves them. The dock
    // at the foot is sticky and holds two parts: the turn line, which
    // steps with the clock, and the bar, which stands only while the
    // reader can act (R2142).
    return '<div class="casino-felt poker">'
      + part('meta', 'class="poker-meta"', metaRow(ctx, t))
      + part('felt', 'class="felt"',
        `<div class="seat-ring">${ring}</div>${boardBox(t)}`)
      + part('hole', 'class="hole"', holeBox(t, me, res))
      + resultRow(ctx, t, me)
      + topUpRow(ctx, t, me)
      + '<div class="casino-bar poker-dock">'
      + turnRow(ctx, t, me)
      + (pokerCanAct(t) ? part('bar', 'class="poker-acts"', barRow(ctx, t, p, size)) : '')
      + '</div>'
      + '</div>';
  },

  act(op: string, btn: HTMLButtonElement, key: string, ctx: GameCtx): boolean {
    const t = ctx.slice.table;
    if (!t) return false;
    const me = casinoSeatOf(t);
    const p = presetsOf(t, me);
    switch (op) {
      case 'pfold':
        sendFold(ctx, t);
        return true;
      case 'pcheck': return sendIf(ctx, t, CasinoAction.CASINO_ACTION_CHECK);
      case 'pcall': return sendIf(ctx, t, CasinoAction.CASINO_ACTION_CALL, owedOf(t));
      case 'pbet':
        return sendSize(ctx, t, key);
      case 'ppreset':
        setSize(t, p, Number(btn.dataset.id ?? p.min));
        return true;
      case 'size':
        setSize(t, p, Number((btn as unknown as HTMLInputElement).value ?? p.min));
        return true;
      case 'ptopup':
        if (casinoOffers(t, CasinoAction.CASINO_ACTION_TOP_UP)) {
          const stake = stakeAt(ctx.rules, t.stakeTier);
          const want = stake ? topUpWant(stake, me) : 0;
          if (want > 0) send(ctx, CasinoAction.CASINO_ACTION_TOP_UP, want);
        }
        return true;
      default:
        return false;
    }
  },

  key(id: MapKeyId, ctx: GameCtx): boolean {
    const t = ctx.slice.table;
    if (!t) return false;
    switch (id) {
      case 'casino_fold':
        return sendFold(ctx, t);
      // One key for both answers to a bet, because the reader presses it
      // with the same meaning: stay in the hand for what it costs.
      case 'casino_call':
        if (allows(t, CasinoAction.CASINO_ACTION_CALL)) {
          send(ctx, CasinoAction.CASINO_ACTION_CALL, owedOf(t));
          return true;
        }
        if (allows(t, CasinoAction.CASINO_ACTION_CHECK)) {
          send(ctx, CasinoAction.CASINO_ACTION_CHECK);
          return true;
        }
        return false;
      case 'casino_bet':
        if (!canSize(t)) return false;
        return sendSize(ctx, t, ALL_IN_KEY);
      default:
        return false;
    }
  },
};
